import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Switch, Redirect, Route, useLocation } from "react-router-dom";
import { connect } from "react-redux";

import { ContentHolder, Loading } from "commons";
import { isPrintMode } from "commons/src/QueryParameter";
import { TRANSACTIONS_ROLE } from "commons/src/helpers/RoleHelpers";

import { WelcomeContainer } from "../routes/Welcome/WelcomeContainer";
import { AnalysisViewContainer } from "../routes/Analysis/AnalysisViewContainer";
import { ContactsContainer } from "../routes/Contacts/ContactsContainer";
import { DocumentsContainer } from "../routes/Documents/DocumentsContainer";
import { FolioDetailsContainer } from "../routes/FolioDetails/FolioDetailsContainer";
import { MemoAssetsContainer } from "../routes/MemoAssets/MemoAssetsContainer";
import { NewsContainer } from "../routes/Welcome/NewsContainer";
import { PersonalDetailsContainer } from "../routes/Personal/PersonalDetailsContainer";
import { AuthorisationsContainer } from "../routes/Authorisations/AuthorisationsContainer";
import { TransactionsContainer } from "../routes/Transactions/TransactionsContainer";
import { IncomeHistoryContainer } from "../routes/Transactions/IncomeHistoryContainer";
import { HomeContainer } from "../routes/Home/HomeContainer";
import { Legal } from "../routes/Legal/Legal";
import { SettingsContainer } from "../routes/Settings/SettingsContainer";
import { initialDataLoad } from "./DataOperations";
import { PrivateRoute } from "./PrivateRoute";
import { Header } from "./Header";
import { NavBar } from "./NavBar";
import { FourOhFour } from "./FourOhFour";
import { routes } from "../helpers/RouteHelpers";

function ClientRoutes({ clientId, theme, roles, isFatal, children }) {
    const location = useLocation();

    useEffect(() => {
        if (clientId && !isPrintMode() && !location?.state?.is404Redirect) {
            initialDataLoad(clientId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    if (!clientId || isFatal) {
        return (
            <Redirect
                to={{
                    pathname: routes.public.login(),
                    state: isFatal ? {} : { nextPathname: location.pathname }
                }}
            />
        );
    }

    if (!theme.isThemeReady) {
        return (
            <LoadingHolder>
                <Loading size={30} colour={theme.selectedTheme.secondary} />
            </LoadingHolder>
        );
    }

    return (
        <AppContent>
            <Header theme={theme.selectedTheme} />
            <Page>
                <NavBar roles={roles} />
                <PageContent>
                    <Switch>
                        {children}
                        <PrivateRoute
                            exact
                            path={routes.client.root()}
                            component={WelcomeContainer}
                        />
                        <PrivateRoute
                            path={routes.client.analysis()}
                            component={AnalysisViewContainer}
                        />
                        <PrivateRoute
                            path={routes.client.contacts()}
                            component={ContactsContainer}
                        />
                        <PrivateRoute
                            path={routes.client.documents()}
                            component={DocumentsContainer}
                        />
                        <PrivateRoute
                            path={routes.client.folioDetails()}
                            component={FolioDetailsContainer}
                        />
                        <PrivateRoute
                            path={routes.client.memoAssets()}
                            component={MemoAssetsContainer}
                        />
                        <PrivateRoute path={routes.client.news()} component={NewsContainer} />
                        <PrivateRoute
                            path={routes.client.personal()}
                            component={PersonalDetailsContainer}
                        />
                        <PrivateRoute
                            path={routes.client.authorisations()}
                            component={AuthorisationsContainer}
                        />
                        <PrivateRoute
                            path={routes.client.transactions()}
                            requiresRole={TRANSACTIONS_ROLE}
                            component={TransactionsContainer}
                        />
                        <PrivateRoute
                            path={routes.client.incomeHistory()}
                            component={IncomeHistoryContainer}
                        />
                        <PrivateRoute
                            path={routes.client.settings()}
                            component={SettingsContainer}
                        />
                        <PrivateRoute path={routes.client.home()} component={HomeContainer} />
                        <PrivateRoute path={routes.client.legal()} component={Legal} />
                        <Route component={FourOhFour} />
                    </Switch>
                </PageContent>
            </Page>
        </AppContent>
    );
}

const Page = styled.div`
    display: flex;
    width: 100%;
`;

const PageContent = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const AppContent = styled.div`
    width: 100%;
    height: 100%;
`;

const LoadingHolder = styled(ContentHolder)`
    text-align: center;
    margin-top: 10%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
`;

ClientRoutes.propTypes = {
    clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roles: PropTypes.arrayOf(PropTypes.string),
    theme: PropTypes.shape({
        selectedTheme: PropTypes.object,
        isThemeReady: PropTypes.bool
    }),
    isFatal: PropTypes.bool,
    adviserLocationId: PropTypes.number,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array, PropTypes.object]),
    adviserId: PropTypes.string
};

const mapStateToProps = state => ({
    clientId: state.user.id,
    roles: state.user.roles,
    theme: state.theme,
    isFatal: state.applicationContext.isFatal,
    adviserLocationId: state.adviser?.adviser?.location?.locationId,
    adviserId: state.user?.adviserId
});

export const ClientRoutesContainer = connect(mapStateToProps)(ClientRoutes);
